import { Component, ViewChild, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { FormControl, NgForm } from '@angular/forms';
import { finalize, map, Observable, startWith } from 'rxjs';
import { BaseTableComponent } from '../../shared/base/base-table.component';
import { Client } from '../../models/clients.model';
import {
  SftpMigrationConfig,
  SaveSftpMigrationConfigRequest,
} from '../../models/sftp-migration-config.model';
import { SftpMigrationConfigService } from '../../services/sftp-migration-config.service';
import { formatHttpErrorMessage } from '../../shared/utils/error.utils';
import { toggleAutocompletePanel } from '../../shared/utils/autocomplete-panel.utils';

const SERVICE_OPTIONS = ['S3-S3'];
const DEFAULT_SERVICE = 'S3-S3';

interface DialogModel {
  pk?: string;
  client: string;
  sourcePrefix: string;
  destinationPrefix: string;
  enabled: boolean;
  service: string;
}

@Component({
  selector: 'app-sftp-migration-configuration',
  standalone: false,
  templateUrl: './sftp-migration-configuration.component.html',
  styleUrls: ['./sftp-migration-configuration.component.css'],
})
export class SftpMigrationConfigurationComponent extends BaseTableComponent<SftpMigrationConfig> {
  override sortBy = 'client';
  override sortDir: 'asc' | 'desc' = 'asc';

  readonly displayedColumns: string[] = [
    'client',
    'sourcePrefix',
    'destinationPrefix',
    'enabled',
    'service',
    'actions',
  ];

  readonly serviceOptions = SERVICE_OPTIONS;

  clients: Client[] = [];
  clientsLoaded = false;
  dialogError = '';
  isEditMode = false;

  model: DialogModel = this.emptyModel();
  private originalSourcePrefix = '';

  clientControl = new FormControl<string | Client>('');
  filteredClients$: Observable<Client[]>;

  @ViewChild('configDialog') dialogTemplate!: TemplateRef<any>;
  private dialogRef?: MatDialogRef<any>;

  constructor(
    private sftpService: SftpMigrationConfigService,
    private dialog: MatDialog,
  ) {
    super();
    this.filteredClients$ = this.clientControl.valueChanges.pipe(
      startWith(this.clientControl.value),
      map((value) => this.filterClients(value)),
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadClients();
  }

  protected override loadData(skip: number, count: number): void {
    this.loading = true;

    this.sftpService
      .getConfigs(skip, count, this.sortBy, this.sortDir, this.searchTerm)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.dataSource.data = response.configs;
          this.totalCount = response.totalCount;
          this.errorMessage = '';
        },
        error: (err) => {
          this.errorMessage = formatHttpErrorMessage(
            'Failed to load SFTP migration configurations',
            err,
          );
          this.dataSource.data = [];
          this.totalCount = 0;
        },
      });
  }

  openAdd(): void {
    this.isEditMode = false;
    this.dialogError = '';
    this.errorMessage = '';
    this.model = this.emptyModel();
    this.clientControl.setValue('');
    this.clientControl.enable({ emitEvent: false });
    this.openDialog();
  }

  openEdit(row: SftpMigrationConfig): void {
    this.isEditMode = true;
    this.dialogError = '';
    this.errorMessage = '';
    this.model = {
      pk: row.pk,
      client: row.client,
      sourcePrefix: row.sourcePrefix,
      destinationPrefix: row.destinationPrefix,
      enabled: row.enabled,
      service: row.service,
    };
    this.originalSourcePrefix = row.sourcePrefix;
    this.clientControl.setValue(row.client, { emitEvent: false });
    this.clientControl.disable({ emitEvent: false });
    this.openDialog();
  }

  closeDialog(): void {
    this.dialogRef?.close();
    this.dialogRef = undefined;
  }

  save(form: NgForm): void {
    form.control.markAllAsTouched();
    if (form.invalid) return;
    if (!this.model.client) {
      this.dialogError = 'Client is required.';
      return;
    }

    this.dialogError = '';
    const request: SaveSftpMigrationConfigRequest = {
      Client: this.model.client,
      // Source prefix is immutable after creation.
      SourcePrefix: this.isEditMode
        ? this.originalSourcePrefix
        : this.model.sourcePrefix.trim(),
      DestinationPrefix: this.model.destinationPrefix.trim(),
      Enabled: this.model.enabled,
      Service: this.model.service.trim(),
    };

    if (
      request.SourcePrefix &&
      request.SourcePrefix === request.DestinationPrefix
    ) {
      this.dialogError = 'Source and destination paths must not be the same.';
      return;
    }

    const call = this.isEditMode
      ? this.sftpService.updateConfig({ ...request, PK: this.model.pk })
      : this.sftpService.addConfig(request);

    call.subscribe({
      next: () => {
        this.loadDataWrapper();
        this.closeDialog();
      },
      error: (err) => {
        this.dialogError = formatHttpErrorMessage(
          this.isEditMode
            ? 'Failed to update configuration'
            : 'Failed to add configuration',
          err,
        );
      },
    });
  }

  onClientSelected(client: Client): void {
    this.clientControl.setValue(client);
    this.model.client = client.DBName;
  }

  onClientInput(): void {
    if (typeof this.clientControl.value === 'string') {
      this.model.client = '';
    }
  }

  clearClient(event: Event): void {
    event.stopPropagation();
    if (this.isEditMode) return;

    this.clientControl.setValue('');
    this.model.client = '';
  }

  openOrClosePanel(event: Event, trigger: MatAutocompleteTrigger): void {
    toggleAutocompletePanel(event, trigger);
  }

  displayClient(option: Client | string | null): string {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.DisplayName;
  }

  private filterClients(value: string | Client | null): Client[] {
    const filterValue = this.normalizeClientValue(value);
    if (!filterValue) return this.clients.slice();

    return this.clients.filter(
      (c) =>
        c.DisplayName.toLowerCase().includes(filterValue) ||
        c.DBName.toLowerCase().includes(filterValue),
    );
  }

  private normalizeClientValue(value: string | Client | null): string {
    if (!value) return '';
    return (typeof value === 'string' ? value : value.DisplayName)
      .trim()
      .toLowerCase();
  }

  private openDialog(): void {
    this.ensureClientsLoaded(() => {
      this.dialogRef = this.dialog.open(this.dialogTemplate, {
        width: '580px',
        autoFocus: false,
        restoreFocus: false,
      });
    });
  }

  private ensureClientsLoaded(callback: () => void): void {
    if (this.clientsLoaded) {
      callback();
      return;
    }
    this.sftpService.getClients().subscribe({
      next: (clients) => {
        this.clients = clients;
        this.clientsLoaded = true;
        callback();
      },
      error: (err) => {
        this.errorMessage = formatHttpErrorMessage(
          'Failed to load clients',
          err,
        );
      },
    });
  }

  private loadClients(): void {
    this.ensureClientsLoaded(() => {});
  }

  private emptyModel(): DialogModel {
    return {
      client: '',
      sourcePrefix: '',
      destinationPrefix: '',
      enabled: true,
      service: DEFAULT_SERVICE,
    };
  }
}
